/**
 * MilestoneDetailsPanel — expandable evidence panel for a single milestone.
 *
 * Shows: Best Truth, Evidence Used, Missing Evidence, Contradictions,
 *        Related Entities, and Raw Reference IDs.
 */

import type { MilestoneEvidence } from '../../types/orderInvestigation';
import { Badge } from '../ui/badge';

interface MilestoneDetailsPanelProps {
  evidence?: MilestoneEvidence;
  expanded: boolean;
}

export default function MilestoneDetailsPanel({ evidence, expanded }: MilestoneDetailsPanelProps) {
  if (!evidence) return null;

  return (
    <div
      className={`overflow-hidden transition-all duration-200 ease-in-out ${
        expanded ? 'max-h-150 opacity-100 mt-3' : 'max-h-0 opacity-0'
      }`}
    >
      <div className="bg-card/50 rounded-lg p-4 space-y-3 border border-border/50 text-sm">
        {/* Best Truth */}
        {evidence.bestTruth && (
          <div>
            <h5 className="text-xs font-semibold text-brand uppercase tracking-wide mb-1">
              Best Truth
            </h5>
            <p className="text-foreground">{evidence.bestTruth}</p>
          </div>
        )}

        {/* Evidence Used */}
        {evidence.evidenceUsed && evidence.evidenceUsed.length > 0 && (
          <div>
            <h5 className="text-xs font-semibold text-success uppercase tracking-wide mb-1">
              Evidence Used
            </h5>
            <ul className="space-y-0.5">
              {evidence.evidenceUsed.map((item, i) => (
                <li key={i} className="text-foreground flex items-start gap-1.5">
                  <span className="text-success mt-0.5 shrink-0">•</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Missing Stronger Evidence */}
        {evidence.missingEvidence && evidence.missingEvidence.length > 0 && (
          <div>
            <h5 className="text-xs font-semibold text-warning uppercase tracking-wide mb-1">
              Missing Stronger Evidence
            </h5>
            <ul className="space-y-0.5">
              {evidence.missingEvidence.map((item, i) => (
                <li key={i} className="text-muted-foreground flex items-start gap-1.5">
                  <span className="text-warning mt-0.5 shrink-0">•</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Contradictions */}
        {evidence.contradictions && evidence.contradictions.length > 0 && (
          <div>
            <h5 className="text-xs font-semibold text-destructive uppercase tracking-wide mb-1">
              Contradictions
            </h5>
            <ul className="space-y-0.5">
              {evidence.contradictions.map((item, i) => (
                <li key={i} className="text-destructive/80 flex items-start gap-1.5">
                  <span className="text-destructive mt-0.5 shrink-0">•</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Related Entities */}
        {evidence.relatedEntities && Object.values(evidence.relatedEntities).some(Boolean) && (
          <div>
            <h5 className="text-xs font-semibold text-ring uppercase tracking-wide mb-1">
              Related Entities
            </h5>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {(evidence.relatedEntities.vehicleRegistration || evidence.relatedEntities.vehicleId) && (
                <span className="text-foreground">
                  <span className="text-muted-foreground">Vehicle:</span> {evidence.relatedEntities.vehicleRegistration || evidence.relatedEntities.vehicleId}
                </span>
              )}
              {(evidence.relatedEntities.driverName || evidence.relatedEntities.driverId) && (
                <span className="text-foreground">
                  <span className="text-muted-foreground">Driver:</span> {evidence.relatedEntities.driverName || evidence.relatedEntities.driverId}
                </span>
              )}
              {(evidence.relatedEntities.pickupLocationId || evidence.relatedEntities.deliveryLocationId) && (
                <span className="text-foreground">
                  <span className="text-muted-foreground">Location:</span> {evidence.relatedEntities.pickupLocationId || evidence.relatedEntities.deliveryLocationId}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Raw References */}
        {evidence.rawRefs && evidence.rawRefs.length > 0 && (
          <div>
            <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
              Raw References
            </h5>
            <div className="flex flex-wrap gap-1.5">
              {evidence.rawRefs.map((ref, i) => (
                <Badge key={i} tone="neutral" className="font-mono">
                  {ref.length > 24 ? `${ref.substring(0, 24)}...` : ref}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
